import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaEnvelope, FaEye, FaMousePointer, FaReply, FaExclamationTriangle } from "react-icons/fa";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { selectTheme } from "../../../Store/features/design/designSlice";
import { getCampaignById, updateCampaign, scheduleCampaign, runCampaign } from "../../../Service/user";
import "./CampaignDetail.scss";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Modal from "react-modal";

const CampaignDetail = () => {
    const { campaignId } = useParams();
    const theme = useSelector(selectTheme);
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("overview");
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduledDate, setScheduledDate] = useState(new Date());
    const [scheduledTime, setScheduledTime] = useState(new Date());
    const [isScheduling, setIsScheduling] = useState(false);

    useEffect(() => {
        fetchCampaignDetails();
    }, [campaignId]);

    const fetchCampaignDetails = async () => {
        try {
            setLoading(true);
            const response = await getCampaignById(campaignId);
            if (response.status === 200) {
                setCampaign(response.campaign);
            } else {
                toast.error(response.message || "Failed to fetch campaign details");
            }
        } catch (error) {
            console.error("Error fetching campaign details:", error);
            toast.error("An error occurred while fetching campaign details");
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => {
        navigate("/campaign");
    };

    const handleOpenScheduleModal = () => {
        // Set default time to 30 minutes from now
        const defaultTime = new Date();
        defaultTime.setMinutes(defaultTime.getMinutes() + 30);
        
        setScheduledDate(defaultTime);
        setScheduledTime(defaultTime);
        setShowScheduleModal(true);
    };

    const handleCloseScheduleModal = () => {
        setShowScheduleModal(false);
    };

    const handleScheduleCampaign = async () => {
        if (campaign.campaignStatus !== 'draft') {
            toast.error("Only draft campaigns can be scheduled");
            return;
        }

        // Open the schedule modal
        handleOpenScheduleModal();
    };

    const handleConfirmSchedule = async () => {
        try {
            setIsScheduling(true);
            
            // Combine date and time
            const scheduledDateTime = new Date(scheduledDate);
            scheduledDateTime.setHours(
                scheduledTime.getHours(),
                scheduledTime.getMinutes(),
                0
            );
            
            // Ensure the scheduled time is in the future
            const now = new Date();
            if (scheduledDateTime <= now) {
                toast.error("Please select a future date and time");
                setIsScheduling(false);
                return;
            }

            const response = await scheduleCampaign(campaignId, scheduledDateTime.toISOString());
            if (response.status === 200) {
                toast.success("Campaign scheduled successfully");
                fetchCampaignDetails();
                handleCloseScheduleModal();
            } else {
                toast.error(response.message || "Failed to schedule campaign");
            }
        } catch (error) {
            console.error("Error scheduling campaign:", error);
            toast.error("An error occurred while scheduling the campaign");
        } finally {
            setIsScheduling(false);
        }
    };

    const handleSendNow = async () => {
        if (campaign.campaignStatus !== 'draft' && campaign.campaignStatus !== 'scheduled') {
            toast.error("Only draft or scheduled campaigns can be sent immediately");
            return;
        }

        try {
            const response = await runCampaign(campaignId);
            
            if (response.status === 200) {
                toast.success("Campaign is now sending");
                fetchCampaignDetails();
            } else {
                toast.error(response.message || "Failed to send campaign");
            }
        } catch (error) {
            console.error("Error sending campaign:", error);
            toast.error("An error occurred while sending the campaign");
        }
    };

    const handlePauseCampaign = async () => {
        if (campaign.campaignStatus !== 'sending' && campaign.campaignStatus !== 'scheduled') {
            toast.error("Only sending or scheduled campaigns can be paused");
            return;
        }

        try {
            const response = await updateCampaign(campaignId, {
                campaignStatus: 'paused'
            });
            
            if (response.status === 200) {
                toast.success("Campaign paused successfully");
                fetchCampaignDetails();
            } else {
                toast.error(response.message || "Failed to pause campaign");
            }
        } catch (error) {
            console.error("Error pausing campaign:", error);
            toast.error("An error occurred while pausing the campaign");
        }
    };

    const handleResumeCampaign = async () => {
        if (campaign.campaignStatus !== 'paused') {
            toast.error("Only paused campaigns can be resumed");
            return;
        }

        try {
            const response = await updateCampaign(campaignId, {
                campaignStatus: 'sending'
            });
            
            if (response.status === 200) {
                toast.success("Campaign resumed successfully");
                fetchCampaignDetails();
            } else {
                toast.error(response.message || "Failed to resume campaign");
            }
        } catch (error) {
            console.error("Error resuming campaign:", error);
            toast.error("An error occurred while resuming the campaign");
        }
    };

    const handleCancelCampaign = async () => {
        if (campaign.campaignStatus === 'completed' || campaign.campaignStatus === 'cancelled') {
            toast.error("This campaign cannot be cancelled");
            return;
        }

        try {
            const response = await updateCampaign(campaignId, {
                campaignStatus: 'cancelled'
            });
            
            if (response.status === 200) {
                toast.success("Campaign cancelled successfully");
                fetchCampaignDetails();
            } else {
                toast.error(response.message || "Failed to cancel campaign");
            }
        } catch (error) {
            console.error("Error cancelling campaign:", error);
            toast.error("An error occurred while cancelling the campaign");
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'draft':
                return 'Draft';
            case 'scheduled':
                return 'Scheduled';
            case 'sending':
                return 'Sending';
            case 'completed':
                return 'Completed';
            case 'paused':
                return 'Paused';
            case 'cancelled':
                return 'Cancelled';
            default:
                return 'Unknown';
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'draft':
                return 'status-draft';
            case 'scheduled':
                return 'status-scheduled';
            case 'sending':
                return 'status-sending';
            case 'completed':
                return 'status-completed';
            case 'paused':
                return 'status-paused';
            case 'cancelled':
                return 'status-cancelled';
            default:
                return '';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    if (loading) {
        return (
            <div className={`campaign-detail ${theme}`}>
                <div className="loading-state">Loading campaign details...</div>
            </div>
        );
    }

    if (!campaign) {
        return (
            <div className={`campaign-detail ${theme}`}>
                <div className="error-state">Campaign not found</div>
            </div>
        );
    }

    return (
        <div className={`campaign-detail ${theme}`}>
            <div className="campaign-header">
                <button className="back-button" onClick={handleGoBack}>
                    <FaArrowLeft className="back-icon" /> Back to Campaigns
                </button>
                <div className={`campaign-status ${getStatusClass(campaign.campaignStatus)}`}>
                    {getStatusLabel(campaign.campaignStatus)}
                </div>
            </div>

            <div className="campaign-title-section">
                <h1>{campaign.campaignName}</h1>
                <div className="campaign-meta">
                    <span>Created: {formatDate(campaign.createdAt)}</span>
                    {campaign.scheduledAt && (
                        <span>Scheduled: {formatDate(campaign.scheduledAt)}</span>
                    )}
                    {campaign.sentAt && (
                        <span>Sent: {formatDate(campaign.sentAt)}</span>
                    )}
                </div>
            </div>

            {/* Campaign Actions */}
            <div className="campaign-actions">
                {campaign.campaignStatus === 'draft' && (
                    <>
                        <button className="action-button schedule" onClick={handleScheduleCampaign}>
                            Schedule
                        </button>
                        <button className="action-button send" onClick={handleSendNow}>
                            Send Now
                        </button>
                    </>
                )}
                {campaign.campaignStatus === 'scheduled' && (
                    <>
                        <button className="action-button send" onClick={handleSendNow}>
                            Send Now
                        </button>
                        <button className="action-button cancel" onClick={handleCancelCampaign}>
                            Cancel
                        </button>
                    </>
                )}
                {campaign.campaignStatus === 'sending' && (
                    <button className="action-button pause" onClick={handlePauseCampaign}>
                        Pause
                    </button>
                )}
                {campaign.campaignStatus === 'paused' && (
                    <>
                        <button className="action-button resume" onClick={handleResumeCampaign}>
                            Resume
                        </button>
                        <button className="action-button cancel" onClick={handleCancelCampaign}>
                            Cancel
                        </button>
                    </>
                )}
            </div>

            <div className="campaign-tabs">
                <button 
                    className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button 
                    className={`tab-button ${activeTab === 'recipients' ? 'active' : ''}`}
                    onClick={() => setActiveTab('recipients')}
                >
                    Recipients
                </button>
                <button 
                    className={`tab-button ${activeTab === 'content' ? 'active' : ''}`}
                    onClick={() => setActiveTab('content')}
                >
                    Email Content
                </button>
            </div>

            <div className="campaign-content">
                {activeTab === 'overview' && (
                    <div className="overview-tab">
                        <div className="metrics-cards">
                            <div className="metric-card">
                                <div className="metric-icon sent">
                                    <FaEnvelope />
                                </div>
                                <div className="metric-details">
                                    <h3>Sent</h3>
                                    <div className="metric-value">{campaign.metrics?.totalSent || 0}</div>
                                </div>
                            </div>
                            <div className="metric-card">
                                <div className="metric-icon opened">
                                    <FaEye />
                                </div>
                                <div className="metric-details">
                                    <h3>Opened</h3>
                                    <div className="metric-value">{campaign.metrics?.totalOpened || 0}</div>
                                    <div className="metric-rate">
                                        {campaign.metrics?.totalSent 
                                            ? Math.round((campaign.metrics.totalOpened / campaign.metrics.totalSent) * 100) 
                                            : 0}%
                                    </div>
                                </div>
                            </div>
                            <div className="metric-card">
                                <div className="metric-icon clicked">
                                    <FaMousePointer />
                                </div>
                                <div className="metric-details">
                                    <h3>Clicked</h3>
                                    <div className="metric-value">{campaign.metrics?.totalClicked || 0}</div>
                                    <div className="metric-rate">
                                        {campaign.metrics?.totalSent 
                                            ? Math.round((campaign.metrics.totalClicked / campaign.metrics.totalSent) * 100) 
                                            : 0}%
                                    </div>
                                </div>
                            </div>
                            <div className="metric-card">
                                <div className="metric-icon replied">
                                    <FaReply />
                                </div>
                                <div className="metric-details">
                                    <h3>Replied</h3>
                                    <div className="metric-value">{campaign.metrics?.totalReplied || 0}</div>
                                    <div className="metric-rate">
                                        {campaign.metrics?.totalSent 
                                            ? Math.round((campaign.metrics.totalReplied / campaign.metrics.totalSent) * 100) 
                                            : 0}%
                                    </div>
                                </div>
                            </div>
                            <div className="metric-card">
                                <div className="metric-icon bounced">
                                    <FaExclamationTriangle />
                                </div>
                                <div className="metric-details">
                                    <h3>Bounced</h3>
                                    <div className="metric-value">{campaign.metrics?.totalBounced || 0}</div>
                                    <div className="metric-rate">
                                        {campaign.metrics?.totalSent 
                                            ? Math.round((campaign.metrics.totalBounced / campaign.metrics.totalSent) * 100) 
                                            : 0}%
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="campaign-lists">
                            <h3>Investor Lists</h3>
                            <div className="lists-container">
                                {campaign.investorLists?.map(list => (
                                    <div key={list._id} className="list-item">
                                        <div className="list-name">{list.name}</div>
                                        <div className="list-description">{list.description}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'recipients' && (
                    <div className="recipients-tab">
                        <div className="recipients-header">
                            <h3>Recipients ({campaign.investors?.length || 0})</h3>
                        </div>
                        <table className="recipients-table">
                            <thead>
                                <tr>
                                    <th>Recipient</th>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th>Opened</th>
                                    <th>Clicked</th>
                                    <th>Replied</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Investors DATA */}
                                {campaign.investors?.map(investor => (
                                    <tr key={investor.InvestorId?._id || investor._id}>
                                        <td>
                                            {investor.InvestorId?.firstName || investor.VCId?.name || 'N/A'} {investor.InvestorId?.lastName || ''}
                                        </td>
                                        <td>{investor.InvestorId?.email || investor.VCId?.email || 'N/A'}</td>
                                        <td>
                                            <span className={`status-badge ${investor.emailStatus || 'pending'}`}>
                                                {investor.emailStatus || 'pending'}
                                            </span>
                                        </td>
                                        <td>{investor.openedAt ? formatDate(investor.openedAt) : 'No'}</td>
                                        <td>{investor.clickedAt ? formatDate(investor.clickedAt) : 'No'}</td>
                                        <td>{investor.repliedAt ? formatDate(investor.repliedAt) : 'No'}</td>
                                    </tr>
                                ))}

                                {/* VC data */}
                                {/* {campaign.investors?.map(investor => (
                                        <tr key={investor.VCId?._id || investor._id}>
                                        <td>
                                            {investor.VCId?.name || 'N/A'}
                                        </td>
                                        <td>{investor.VCId?.email || 'N/A'}</td>
                                        <td>
                                            <span className={`status-badge ${investor.emailStatus || 'pending'}`}>
                                                {investor.emailStatus || 'pending'}
                                            </span>
                                        </td>
                                        <td>{investor.openedAt ? formatDate(investor.openedAt) : 'No'}</td>
                                        <td>{investor.clickedAt ? formatDate(investor.clickedAt) : 'No'}</td>
                                        <td>{investor.repliedAt ? formatDate(investor.repliedAt) : 'No'}</td>
                                    </tr>
                                    ))
                                } */}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'content' && (
                    <div className="content-tab">
                        <div className="email-preview">
                            <div className="email-subject">
                                <h3>Subject</h3>
                                <div className="subject-text">{campaign.template?.emailSubject}</div>
                            </div>
                            {campaign.cc?.length > 0 && (
                                <div className="email-field">
                                    <h3>CC</h3>
                                    <div className="email-addresses">
                                        {campaign.cc.map((email, index) => (
                                            <span key={index} className="email-chip">
                                                {email}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {campaign.bcc?.length > 0 && (
                                <div className="email-field">
                                    <h3>BCC</h3>
                                    <div className="email-addresses">
                                        {campaign.bcc.map((email, index) => (
                                            <span key={index} className="email-chip">
                                                {email}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                           
                            <div className="email-body">
                                <h3>Body</h3>
                                <div 
                                    className="body-content"
                                    dangerouslySetInnerHTML={{ __html: campaign.template?.emailBody }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Schedule Modal */}
            <Modal
                isOpen={showScheduleModal}
                onRequestClose={handleCloseScheduleModal}
                contentLabel="Schedule Campaign"
                className="schedule-modal"
                overlayClassName="schedule-modal-overlay"
            >
                <div className="schedule-modal-content">
                    <h2>Schedule Campaign</h2>
                    <p>Select when you want to send this campaign:</p>
                    
                    <div className="schedule-form">
                        <div className="form-group">
                            <label>Date:</label>
                            <DatePicker
                                selected={scheduledDate}
                                onChange={date => setScheduledDate(date)}
                                minDate={new Date()}
                                dateFormat="MMMM d, yyyy"
                                className="date-picker"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Time:</label>
                            <DatePicker
                                selected={scheduledTime}
                                onChange={time => setScheduledTime(time)}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                                className="time-picker"
                            />
                        </div>
                    </div>
                    
                    <div className="schedule-actions">
                        <button 
                            className="cancel-button" 
                            onClick={handleCloseScheduleModal}
                            disabled={isScheduling}
                        >
                            Cancel
                        </button>
                        <button 
                            className="confirm-button" 
                            onClick={handleConfirmSchedule}
                            disabled={isScheduling}
                        >
                            {isScheduling ? 'Scheduling...' : 'Schedule Campaign'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CampaignDetail; 